/**
 * Customer.io domain types — event names + Person/Account trait shapes.
 * Shared by the server integration, the API route, and the client wrapper.
 */

import { z } from 'zod'

export const CustomerIOEventName = {
  // Acquisition
  UserCreated: 'user_created',
  UserFirstSignedIn: 'user_first_signed_in',
  // Onboarding
  EventCreated: 'event_created',
  // Activation cluster
  SpaceAddedToEvent: 'space_added_to_event',
  GhostVenueCreated: 'ghost_venue_created',
  RecsReceived: 'recs_received',
  RevelRecsBriefSubmitted: 'revel_recs_brief_submitted',
  SrpSpaceCardViewMoreSpaces: 'srp_space_card_view_more_spaces',
  // Activation moment
  InquirySent: 'inquiry_sent',
  // Post-activation depth
  ProposalAssistantUsed: 'proposal_assistant_used',
  TravelTimesCalculated: 'travel_times_calculated',
  CollectionCreated: 'collection_created',
  // Stakeholder share
  EmailSummaryGenerated: 'email_summary_generated',
  PresentationExported: 'presentation_exported',
  PresentationGenerated: 'presentation_generated',
  // Outcome
  SpaceMarkedBooked: 'space_marked_booked',
  EventMarkedBooked: 'event_marked_booked',
} as const

export const CustomerIOEventSchema = z.enum(CustomerIOEventName)

export type CustomerIOEventName = z.infer<typeof CustomerIOEventSchema>

/**
 * Person traits — see REV-938 for sources. All optional on the wire because
 * different call sites have access to different subsets; the server fills in
 * the rest from the user record.
 */
export interface CustomerIOPersonTraits {
  email?: string
  supabase_user_id?: string | null
  legacy_user_id?: number | null
  first_name?: string | null
  last_name?: string | null
  title?: string | null
  office_phone?: string | null
  mobile_phone?: string | null
  is_active?: boolean
  email_verified?: boolean
  created_at?: string | null
  last_login_at?: string | null
  account_id?: number | null
  is_admin?: boolean
}

export interface CustomerIOAccountTraits {
  name?: string | null
  account_id?: number
  entity_name?: string | null
  legal_name?: string | null
  entity_type?: string | null
  plan?: string | null
  plan_status?: string | null
  trial_start_at?: string | null
  trial_end_at?: string | null
  subscription_start_at?: string | null
  subscription_end_at?: string | null
  is_paid?: boolean
  created_at?: string | null
  venue_groups_active?: string[]
}
